import { useCallback, useState } from 'react';
import { useAuthContext } from '../context/AuthContext';
import { useNotificationContext } from '../context/NotificationContext';
import { ApiService } from '../services/api';
import { ProductRecognitionResponse } from '../types';

const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

export const useProductRecognition = () => {
  const { token } = useAuthContext();
  const { showErrorToast } = useNotificationContext();

  const [productRecognitionData, setProductRecognitionData] =
    useState<ProductRecognitionResponse | null>(null);
  const [isRunningModel, setIsRunningModel] = useState(false);
  const [isDataFromCache, setIsDataFromCache] = useState(false);

  /**
   * Loads cached data from sessionStorage if available and valid
   * Returns true if cache was loaded, false otherwise
   */
  const loadFromCache = useCallback((imageId: number): boolean => {
    const cacheKey = `product-recognition-${imageId}`;
    const cachedData = sessionStorage.getItem(cacheKey);

    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        const cacheTime = parsed.timestamp || 0;
        const now = Date.now();

        if (now - cacheTime < CACHE_TTL_MS && parsed.data) {
          setProductRecognitionData(parsed.data);
          setIsDataFromCache(true);
          return true;
        } else {
          // Clear stale cache
          sessionStorage.removeItem(cacheKey);
        }
      } catch (e) {
        sessionStorage.removeItem(cacheKey);
      }
    }

    setProductRecognitionData(null);
    setIsDataFromCache(false);
    return false;
  }, []);

  /**
   * Runs the product recognition model with caching and retry logic
   * - First checks sessionStorage cache
   * - If useApiCache is true and fails, retries without API cache
   * - Stores successful responses in sessionStorage
   */
  const runProductRecognitionModel = useCallback(
    async (
      imageId: number | undefined,
      options: { useApiCache?: boolean; triggerPogCompliance?: boolean } = {}
    ) => {
      const { useApiCache = false, triggerPogCompliance = false } = options;

      if (!imageId || !token) {
        if (!imageId) {
          showErrorToast('No image selected');
        }
        return;
      }

      // Check session storage cache first (skip if triggering pog compliance)
      const cacheKey = `product-recognition-${imageId}`;
      if (!triggerPogCompliance) {
        const cachedData = sessionStorage.getItem(cacheKey);

        if (cachedData) {
          try {
            const parsed = JSON.parse(cachedData);
            const cacheTime = parsed.timestamp || 0;
            const now = Date.now();

            if (now - cacheTime < CACHE_TTL_MS) {
              setProductRecognitionData(parsed.data);
              setIsDataFromCache(true);
              return;
            }
          } catch (e) {
            console.warn('Failed to parse cached data, fetching fresh');
            sessionStorage.removeItem(cacheKey);
          }
        }
      }

      try {
        setIsRunningModel(true);
        const result = await ApiService.runRayProductRecognition(
          token,
          imageId,
          useApiCache,
          triggerPogCompliance
        );

        // Cache the result when not using API cache
        if (!useApiCache) {
          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({
              data: result,
              timestamp: Date.now(),
            })
          );
        }

        setProductRecognitionData(result);
        setIsDataFromCache(false);
      } catch (err) {
        // If useApiCache failed, retry without it
        if (useApiCache) {
          try {
            const result = await ApiService.runRayProductRecognition(
              token,
              imageId,
              false,
              triggerPogCompliance
            );

            // Cache the result
            sessionStorage.setItem(
              cacheKey,
              JSON.stringify({
                data: result,
                timestamp: Date.now(),
              })
            );

            setProductRecognitionData(result);
            setIsDataFromCache(false);
            return;
          } catch (retryErr) {
            showErrorToast(
              retryErr instanceof Error
                ? retryErr.message
                : 'Error running product recognition model'
            );
          }
        } else {
          showErrorToast(
            err instanceof Error ? err.message : 'Error running product recognition model'
          );
        }
      } finally {
        setIsRunningModel(false);
      }
    },
    [token, showErrorToast]
  );

  /**
   * Clears the product recognition cache
   * If imageId is provided, only clears that specific cache entry
   * Otherwise clears all product-recognition cache entries
   */
  const clearCache = useCallback((imageId?: number) => {
    if (imageId) {
      sessionStorage.removeItem(`product-recognition-${imageId}`);
    } else {
      // Clear all product-recognition cache entries
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('product-recognition-')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    }

    setProductRecognitionData(null);
    setIsDataFromCache(false);
  }, []);

  return {
    productRecognitionData,
    setProductRecognitionData,
    isRunningModel,
    isDataFromCache,
    runProductRecognitionModel,
    loadFromCache,
    clearCache,
  };
};
